import { FC, useEffect, useRef, useState } from 'react';
import { SelectionToggleType } from '../../../../enums/SelectionToggleType';
import { useOutsideClickDetection, useSearch } from '../../../../hooks';
import { useCheckDevice } from '../../../../hooks/UseCheckDevice';
import { useHeapTrack } from '../../../../hooks/UseHeapTrack';
import { FilterItemResource } from '../../../../types/FilterTypes';
import { DropdownWrapper, Icon } from '../../../style';
import DropdownSelect from '../dropdown-select/DropdownSelect';
import MultiSelectOptions from '../multi-select-options/MultiSelectOptions';
import Search from '../search/Search';

export type MultiSelectDropdownProps = {
  options: readonly FilterItemResource[];
  values?: readonly FilterItemResource[];
  placeholder: string;
  allowSearchFilter?: boolean;
  enableCheckAll?: boolean;
  displaySelectedLabels?: boolean;
  selectedLabelPosition?: 'bottom' | 'right';
  borderRadius?: number;
  color?: string;
  type: 'am-label' | 'am-dropdown';
  additionalCssClass?: string;
  displaySubLabel?: JSX.Element;
  dynamicRender?: boolean;
  onChange: (values: FilterItemResource[]) => void;
  onUpdate?: (values: FilterItemResource[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
  cssClass?: string;
  optionItemWidth?: number;
  isSearchOnlyText?: boolean;
  selectionToggleType?: SelectionToggleType;
  disabled?: boolean;
  hideUnselectedOption?: boolean;
  strikethroughUnselectedOption?: boolean;
  dataTestId?: string;
  searchType?: 'text' | 'number' | 'email';
};

const MultiSelectDropdown: FC<MultiSelectDropdownProps> = (props) => {
  const wrapperRef = useRef<HTMLElement>(null);
  const storeCode = localStorage.getItem('am.storeCode');

  const [isVisible, setIsVisible] = useState<boolean>(false);
  // const [windowHeight, setWindowHeight] = useState<number>(0);
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const { isMobileDevice } = useCheckDevice();

  useOutsideClickDetection(wrapperRef, setIsVisible);

  const { searchText, setSearchText } = useSearch();

  const handleSearch = (text: string): void => {
    //TODO: Search for a better solution.
    setTimeout(() => {
      setSearchText(text);
    }, 1);
  };

  const label = (): string => {
    const selectedValuesLength = props.values?.length;
    let result: string;
    if (selectedValuesLength > 2) {
      result = `${props.values?.length} Selected`;
    } else if (selectedValuesLength === props.options.length) {
      result = `All`;
    } else {
      result = [...props.values]
        .sort((item1, item2) => (item1.label > item2.label ? 1 : -1))
        .map((item) => item.label)
        .join(', ');
    }

    return result;
  };

  const inlineLabel = (): string => {
    const filteredSelectedOptions = props.values?.length
      ? props.values.filter((x) => !!props.options.find((y) => y.key === x.key))
      : [];
    return filteredSelectedOptions?.length
      ? filteredSelectedOptions?.length === props.options.length
        ? `All`
        : `${filteredSelectedOptions?.length} Sel.`
      : ``;
  };

  const handleMouseEvent = (event): void => {
    if (event && event.target) {
      setFilterHeight(event.target.offsetParent?.offsetHeight || 0);
    }
  };

  useEffect(() => {
    if (isVisible && props.onOpen) {
      props.onOpen();
    }
    if (!isVisible && props.onClose) {
      props.onClose();
    }
  }, [isVisible]);

  const handleDropdownWrapperClick = (placeholder?: string): void => {
    setIsVisible(!isVisible);

    if (isVisible) {
      return;
    } else if (placeholder === 'Notes & Insights') {
      useHeapTrack('Mastermind - Notes & Insights', {
        StoreCode: storeCode,
        NotesAndInsightsOpened: true,
      });
    } else if (placeholder === 'Incentives') {
      useHeapTrack('Mastermind - Incentives Filter', {
        StoreCode: storeCode,
        IncentivesFilterOpened: true,
      });
    } else if (placeholder === 'Private Incentives') {
      useHeapTrack('Mastermind - Private Incentives Filter', {
        StoreCode: storeCode,
        PrivateIncentivesFilterOpened: true,
      });
    }

    if (props.options?.length === 0 && props.onUpdate) {
      props.onUpdate([]);
      return;
    }

    const filteredSelectedOptions = props.values.filter(
      (x) => !!props.options.find((y) => y.key === x.key),
    );
    if (filteredSelectedOptions?.length !== props.values?.length && props.onUpdate) {
      props.onUpdate(filteredSelectedOptions);
    }
  };

  const labelProps = props.displaySelectedLabels
    ? props.selectedLabelPosition === 'bottom'
      ? { bottomLabel: label() }
      : { inlineLabel: inlineLabel() }
    : '';

  return (
    <DropdownWrapper
      isMobileDevice={isMobileDevice}
      data-testid={props.dataTestId || 'multi-select-dropdown'}
      className={
        props.type === 'am-label'
          ? `multi-select label-type ${props.cssClass}`
          : `multi-select ${props.cssClass}`
      }
      ref={wrapperRef}
      flexDirection='row'
      borderRadius={props.borderRadius}
      disabled={props.disabled}
    >
      <div className={`heap-filter-item ${props.type === 'am-dropdown' ? 'w-88' : ''}`}>
        {props.type === 'am-label' && (
          <>
            <span data-testid='multi-select-dropdown-header'>
              <span className='header-text header-sort'>{props.placeholder}</span>
              <div
                className='header-filter-value'
                onClick={() => handleDropdownWrapperClick(props.placeholder)}
                onMouseDown={handleMouseEvent}
              >
                {props.displaySubLabel}
              </div>
            </span>
          </>
        )}
        {props.type === 'am-dropdown' && (
          <span
            data-testid='multi-select-dropdown-header'
            onClick={() => handleDropdownWrapperClick(props.placeholder)}
            onMouseDown={handleMouseEvent}
          >
            <DropdownSelect
              name={props.placeholder}
              color={props.color}
              {...labelProps}
            ></DropdownSelect>
          </span>
        )}
        {isVisible && (
          <div
            className={
              props.type === 'am-label'
                ? `label-multi-select-filter unflex ${props.additionalCssClass}`
                : ''
            }
          >
            <MultiSelectOptions
              options={props.options}
              values={props.values}
              filterHeight={filterHeight}
              allowSearchFilter={props.allowSearchFilter}
              enableCheckAll={props.enableCheckAll}
              onChange={props.onChange}
              cssClass={props.type === 'am-label' ? 'unflex label-type' : ''}
              searchText={searchText}
              type={props.type}
              optionItemWidth={props.optionItemWidth}
              dynamicRender={props.dynamicRender}
              selectionToggleType={props.selectionToggleType}
              hideUnselectedOption={props.hideUnselectedOption}
              strikethroughUnselectedOption={props.strikethroughUnselectedOption}
            >
              {props.children}
              {props.allowSearchFilter && props.options && props.options?.length > 0 && (
                <Search
                  cssClass={props.type === 'am-label' ? 'unflex exclude-hover' : 'exclude-hover'}
                  placeholder={props.isSearchOnlyText ? 'Search' : 'Search ' + props.placeholder}
                  search={searchText}
                  setSearch={handleSearch}
                  type={props.searchType ? props.searchType : 'text'}
                />
              )}
            </MultiSelectOptions>
          </div>
        )}
      </div>
      {props.type === 'am-dropdown' && (
        <div className='vertical-center'>
          <Icon type='dropdown' size={14} />
        </div>
      )}
    </DropdownWrapper>
  );
};

export default MultiSelectDropdown;
